import { UserActionTypes } from "./actionTypes";
import {
    accept_policies,
    checkAuth,
    create_user,
    delete_user,
    get_all_users,
    login_user,
    update_user
} from "../Services/user.service";
import { message } from "antd";
import i18n from "../i18n";
import { getAccessToken, setAccessToken } from "../utils/accessToken";
import { isCookieAuthMode } from "../utils/authMode";
import { clearKnownAuthSession, hasKnownAuthSession } from "../utils/authSessionState";
import { clearCommercialControl } from "./commercialControl.action";

export const EMAIL_VERIFICATION_REQUIRED = "EMAIL_VERIFICATION_REQUIRED";

const getBackendErrorCode = (err) => {
    const data = err?.response?.data || {};
    const detail = data?.detail;
    if (detail && typeof detail === "object") {
        return detail?.code || detail?.error || detail?.message;
    }
    return data?.code || data?.error || detail || data?.message;
};

export const isEmailVerificationRequiredError = (err) =>
    err?.response?.status === 403 &&
    String(getBackendErrorCode(err) || "").trim().toUpperCase() === EMAIL_VERIFICATION_REQUIRED;

export const getLoginErrorMessage = (err) => {
    if (!err.response) {
        if (err.code === "ECONNABORTED") {
            return i18n.t("login:errors.timeout");
        }
        return i18n.t("login:errors.backendUnreachable");
    }

    const status = err.response.status;
    const backendMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.detail;

    if (isEmailVerificationRequiredError(err)) {
        return i18n.t("login:emailVerification.requiredMessage");
    }
    if (status === 401) {
        return i18n.t("login:errors.invalidCredentials");
    }
    if (status === 404) {
        return i18n.t("login:errors.invalidCredentials");
    }
    if (status === 403) {
        return backendMessage || i18n.t("login:errors.accountDisabled");
    }
    if (status === 422) {
        return i18n.t("login:errors.invalidEmail");
    }
    if (status >= 500) {
        return i18n.t("login:errors.serverUnavailable");
    }

    return backendMessage || i18n.t("login:errors.generic");
};

export const getAuthCheckErrorMessage = (err) => {
    const backendMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.detail;
    const normalizedMessage = String(backendMessage || "").trim().toLowerCase();

    if (
        err.response?.status === 401 ||
        normalizedMessage.includes("token manquant") ||
        normalizedMessage.includes("token invalide") ||
        normalizedMessage.includes("missing token") ||
        normalizedMessage.includes("invalid token")
    ) {
        return i18n.t("common:general.session_expired_reconnect");
    }

    return backendMessage || i18n.t("users:messages.authError");
};

export const RequestCheckAuth = (navigate, redirectToLandingPage) => {
    return async (dispatch) => {
        const token = getAccessToken();
        const hadKnownSession = hasKnownAuthSession(token);
        if (!token && !isCookieAuthMode()) {
            navigate("/");
            return;
        }

        dispatch(setLoading(true));
        try {
            const authResponse = await checkAuth();
            const authPayload = authResponse?.success !== undefined ? authResponse : authResponse?.data;
            if (authPayload?.success && authPayload.data?.is_authenticated) {
                // Stocker les infos du user (role, client_id, etc.) dans Redux
                if (authPayload.data.user) {
                    dispatch({ type: UserActionTypes.SET_CURRENT_USER, payload: authPayload.data.user });
                    dispatch(setPolicyAcceptance(authPayload.data.user));
                }
                redirectToLandingPage(authPayload.data.user);
            } else {
                dispatch(setAuth(false));
                navigate("/");
            }
        } catch (err) {
            if (hadKnownSession) {
                dispatch(setError(getAuthCheckErrorMessage(err)));
                clearKnownAuthSession();
            }
            dispatch(setAuth(false));
            navigate("/");
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const RequestGetAllUsers = () => {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const res = await get_all_users();
            const data = res?.data;

            if (data?.success && data?.data) {
                dispatch(setUsers(data.data));
            } else {
                dispatch(setError(
                    data?.error || data?.message || i18n.t("users:messages.loadError")
                ));
            }
        } catch (err) {
            dispatch(setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("users:messages.serverError")
            ));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const RequestDeleteUser = (userId) => {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const res = await delete_user(userId);
            const result = res?.success !== undefined ? res : res?.data;

            if (result?.success) {
                dispatch({
                    type: UserActionTypes.DELETE_ONE,
                    payload: userId
                });
                message.success(result.message || i18n.t("users:messages.deleteSuccess"));
                return { success: true, data: result.data };
            } else {
                const errorMessage = result?.error || result?.message || i18n.t("users:messages.deleteUnknown");
                message.error(errorMessage);
                dispatch(setError(errorMessage));
                return { success: false, error: errorMessage };
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("users:messages.deleteError");
            message.error(errorMessage);
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };
};


export const RequestCreateUser = (data, onSuccess) => {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const result = await create_user(data);

            if (result?.success && result?.data) {
                dispatch({
                    type: UserActionTypes.ADD_ONE,
                    payload: result.data,
                });
                message.success(i18n.t("users:messages.createSuccess"));
                if (onSuccess) onSuccess();
            } else {
                dispatch(setError(
                    result?.error || result?.message || i18n.t("users:messages.createUnknown")
                ));
            }
        } catch (err) {
            dispatch(setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("users:messages.createError")
            ));
        } finally {
            dispatch(setLoading(false));
        }
    };
};



export const RequestUpdateUser = (userId, data, onSuccess) => {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const result = await update_user(userId, data);

            if (result?.success && result?.data) {
                dispatch({
                    type: UserActionTypes.UPDATE_ONE,
                    payload: result.data,
                });
                message.success(i18n.t("users:messages.updateSuccess"));
                if (onSuccess) onSuccess();
                return { success: true, data: result.data };
            } else {
                const errorMessage = result?.error || result?.message || i18n.t("users:messages.updateUnknown");
                message.error(errorMessage);
                dispatch(setError(errorMessage));
                return { success: false, error: errorMessage };
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("users:messages.updateError");
            message.error(errorMessage);
            dispatch(setError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };
};



export const persistAuthenticatedSession = (dispatch, authData) => {
    const { access_token, id, full_name } = authData;
    dispatch(clearCommercialControl());
    setAccessToken(access_token);
    sessionStorage.setItem("user_id", id);
    sessionStorage.setItem("nom_complet", full_name);
    dispatch({ type: UserActionTypes.SET_CURRENT_USER, payload: authData });
    dispatch(setFirstLogin(authData.first_login));
    dispatch(setPolicyAcceptance(authData));
};

export const RequestLogin = (username, password, redirectToLandingPage) => {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const res = await login_user(username, password);
            persistAuthenticatedSession(dispatch, res.data);
            redirectToLandingPage(res.data);
            return { success: true, data: res.data };
        } catch (err) {
            const errorMessage = getLoginErrorMessage(err);
            dispatch(setError(errorMessage));
            if (isEmailVerificationRequiredError(err)) {
                return {
                    success: false,
                    code: EMAIL_VERIFICATION_REQUIRED,
                    error: errorMessage,
                };
            }
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setLoading(false));
        }
    };
};

// actions redux inchangées
export const setFirstLogin = (value) => ({
    type: UserActionTypes.SET_FIRST_LOGIN,
    payload: value,
});

export const setPolicyAcceptance = (payload) => ({
    type: UserActionTypes.SET_POLICY_ACCEPTANCE,
    payload,
});

export const RequestAcceptPolicies = () => {
    return async (dispatch) => {
        const result = await accept_policies();
        const payload = result?.data || {};
        dispatch(setPolicyAcceptance({
            ...payload,
            requires_policy_acceptance: false,
        }));
        return result;
    };
};

export const setAuth = (isAuthenticated) => ({
    type: UserActionTypes.SET_AUTH,
    payload: isAuthenticated,
});

export const setUsers = (users) => ({
    type: UserActionTypes.SET_USERS,
    payload: users,
});

export const setLoading = (isLoading) => ({
    type: UserActionTypes.SET_LOADING,
    payload: isLoading,
});

export const setLoadingAuth = (isLoading) => ({
    type: UserActionTypes.SET_LOADING_AUTH,
    payload: isLoading,
});

export const setError = (error) => ({
    type: UserActionTypes.SET_ERROR,
    payload: error,
});

export const setUser = (user) => ({
    type: UserActionTypes.SET_USER,
    payload: user,
});

export const setCurrentUser = (user) => ({
    type: UserActionTypes.SET_CURRENT_USER,
    payload: user,
});
